import React from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BoltIcon from "@mui/icons-material/Bolt";
import { formatDate, formatDateTimeWithSeconds } from "../../utils/DateTimeUtils";
import { maskPhoneNumber } from "../../utils/PhoneNumberUtils";
import { EditButton, useRecordContext, useRefresh } from "react-admin";
import { CookService } from "../../services/CookServices";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import CheckInAndOutImages from "../Common/CheckInAndOutImages";
import { useCallback, useEffect, useState } from "react";
import { OneMealService } from "../../services/OneMealService";
import ChefForPartStatusEnum from "../../enums/ChefForPartStatusEnum";
import CustomCallIconButton from "../Common/CallButton/CustomCallIconButton";
import { UserType } from "../../enums/common";
import PartyJsonViewer from "../ChefForParty/PartyJsonViewer";
import ImagePreviewer from "../Common/ImagePreviewer";
import { useAppSelector } from "../../store/hooks";
import GroupPermissionFunction from "../../utils/groupPermissionFunction";
import { UserGroupsEnum } from "../../enums/UserGroupsEnum";
import { OneMealServiceStatus } from "../../enums/OneMealServiceStatus";
import MealMapTable from "../Common/MealMapTable";
import AddMealMapDialog from "../Common/AddMealMapDialog";
import CustomerPreferenceSummary from "../Customers/CustomerPreferenceSummary";
import CancelledChefitBanner from "./CancelledChefitBanner";
import RescheduledChefitBanner from "./RescheduledChefitBanner";
import { useContext } from "react";
import { ImportantVisitContext } from "./ImportantVisitContext";
import { colors } from "../../styles/colors";

// Common Styles
import {
  CustomTableRowTR,
  HorizontalContainer,
  HorizontalLine,
  LeftWorkspacePanel,
  RightWorkspacePanel,
  SectionHeader,
  SectionTitle,
  ShowLabel,
  ShowValue,
  StatusBooleanBadge,
  StyledTable,
  TableData,
  TableHeader,
  TableTopHeader,
  TableWrapper,
  ThreeColGrid,
  Title,
  WorkspacePanel,
  WorkspacePanelsWrapper,
  WorkspaceSection,
  WorkSpaceTitleRow,
} from "../../styles/commonStyles";

// Icons
import {
  AlertIcon,
  CalendarIcon,
  ChefCapIcon,
  CityIcon,
  ClockIcon,
  HouseIcon,
  LocationIcon,
  RecieptIcon,
  UserIcon,
} from "../Svg";

const restrictedEditState = ["FULFILLED", "CANCELLED"];
const oneMealServiceApi = new OneMealService();
const cookServiceForRating = new CookService();

const getSourceOfBooking = (clientId: number): string => {
  if (clientId === 2) return "CENTAURUS";
  if (clientId === 4) return "CUSTOMER APP";
  if (clientId === 8) return "WEBSITE";
  return String(clientId);
};

const formatDateTime = (dateStr: string | Date): string => {
  if (!dateStr) return "N/A";
  return (
    new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }) +
    " " +
    new Date(dateStr).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
  );
};

const OneMealServiceMetaShow = () => {
  const record = useRecordContext();
  const [isNew, setIsNew] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [pageSize] = useState(10);
  const userDetails = useAppSelector((state) => state.userDetails.value);
  const refresh = useRefresh();
  const groupsArray: string[] = userDetails.adminUser.accessGroups;

  const getData = useCallback(
    (pageNo: number) => {
      setLoading(true);
      oneMealServiceApi
        .getAllOneMealServiceForHouse(
          record?.houseId,
          pageSize,
          pageSize * pageNo,
          { order: "DESC", field: "createdAt" },
          { status: ChefForPartStatusEnum.FULFILLED },
        )
        .then((data) => {
          setIsNew(data?.data?.length === 0);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    },
    [record?.houseId, pageSize],
  );

  const hasEditAccess = () =>
    GroupPermissionFunction(groupsArray, [UserGroupsEnum.ADMIN]) ||
    record?.status === OneMealServiceStatus.PENDING;

  useEffect(() => {
    getData(0);
  }, [getData]);

  const cookName = record?.cook?.cookFullProfile?.firstName
    ? `${record.cook.cookFullProfile.firstName} ${record.cook.cookFullProfile.lastName || ""}`.trim()
    : "Not Available";

  const isImportant = useContext(ImportantVisitContext);

  const isFirstVisit = isNew || !!(record as any)?.isFirstVisit;

  return (
    <div
      style={{
        position: "relative",
        border: isImportant
          ? "2px solid #FDBA74"
          : isFirstVisit
          ? "2px solid #FCD34D"
          : undefined,
        borderRadius: isImportant || isFirstVisit ? "8px" : undefined,
        padding: isImportant || isFirstVisit ? "12px" : undefined,
        marginTop: isFirstVisit ? "10px" : undefined,
      }}
    >
      {/* "First Visit" label sitting on the top-left border line */}
      {isFirstVisit && !loading && (
        <span
          style={{
            position: "absolute",
            top: "-13px",
            left: "14px",
            backgroundColor: "#FFFBEB",
            padding: "2px 12px",
            fontSize: "13px",
            fontWeight: 700,
            color: "#92400E",
            border: "2px solid #FCD34D",
            borderRadius: "6px",
            letterSpacing: "0.6px",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            lineHeight: "20px",
          }}
        >
          ⭐ First Visit
        </span>
      )}

      <CustomerPreferenceSummary
        customerPreference={record?.customerPreferences}
        customerId={record?.customerId}
      />

      <CancelledChefitBanner record={record} />
      <RescheduledChefitBanner record={record} />

      {/* Edit Button */}
      {!restrictedEditState.includes(record?.status) && hasEditAccess() && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <EditButton />
        </Box>
      )}

      {/* Status Chips */}
      {(isNew || record?.isInstantChefit) && !loading && (
        <Box sx={{ display: "flex", gap: "10px", mb: 1 }}>
          {isNew && (
            <Chip
              label="New Customer (No Fulfilled One Meal Services Yet)"
              sx={{ backgroundColor: "#90ee8f", flex: 1 }}
            />
          )}
          {record?.isInstantChefit && (
            <Chip
              label="Instant Chefit"
              sx={{
                backgroundColor: "#cc338b",
                flex: 1,
                color: "#fff",
                fontWeight: "500",
              }}
            />
          )}
        </Box>
      )}
      {record?.hasOptedPayAfterService && (
        <Chip
          sx={{ width: "100%", mb: 1, backgroundColor: "#ffac5f" }}
          label="Opted for Pay After Service"
        />
      )}
      {record?.razorpayOrderId && (
        <Box display="flex" justifyContent="flex-end">
          <Chip sx={{ mb: 1, backgroundColor: "#ffac5f" }} label="Razor Pay Order Generated" />
        </Box>
      )}

      <WorkspacePanelsWrapper>
        <LeftWorkspacePanel>

          
          <WorkspacePanel>
            <WorkspaceSection>
          <SectionHeader>
            <WorkSpaceTitleRow>
              <CalendarIcon color={colors.primary} height={24} width={24} />
              <SectionTitle>Booking Overview</SectionTitle>
            </WorkSpaceTitleRow>
          </SectionHeader>
        </WorkspaceSection>

        <ThreeColGrid>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>#</ShowLabel>
            <ShowLabel style={{ marginBottom: "0px" }}>ID:</ShowLabel>
            {record?.id ? (
              <Link href={`/#/OneMealService/${record.id}/show`} target="_blank" sx={{ fontWeight: 500 }}>
                {record.id}
              </Link>
            ) : (
              <ShowValue>-</ShowValue>
            )}
            <Tooltip title="Copy ID">
              <IconButton
                size="small"
                onClick={() => navigator.clipboard.writeText(String(record?.id))}
                sx={{ p: 0.25 }}
              >
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </HorizontalContainer>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Status:</ShowLabel>
            <ShowValue>{record?.status || "-"}</ShowValue>
          </HorizontalContainer>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Payment Status:</ShowLabel>
            <ShowValue>{record?.paymentStatus || "-"}</ShowValue>
          </HorizontalContainer>
        </ThreeColGrid>

        <ThreeColGrid>
          <HorizontalContainer>
            <CalendarIcon color={"#6C7C93"} width={16} height={16} />
            <ShowLabel style={{ marginBottom: "0px" }}>Visit Date:</ShowLabel>
            <ShowValue>{record?.date ? formatDate(record.date) : "-"}</ShowValue>
          </HorizontalContainer>
          <HorizontalContainer>
            <ClockIcon color={"#6C7C93"} width={16} height={16} />
            <ShowLabel style={{ marginBottom: "0px" }}>Visit Time:</ShowLabel>
            <ShowValue>{record?.time || "-"}</ShowValue>
          </HorizontalContainer>
          <div>
            <ShowLabel style={{ marginBottom: "4px" }}>Booking Time</ShowLabel>
            <ShowValue style={{ fontWeight: 500 }}>
              {record?.bookingTime ? formatDateTime(record.bookingTime) : "-"}
            </ShowValue>
          </div>
        </ThreeColGrid>

        <ThreeColGrid>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Source:</ShowLabel>
            <ShowValue>
              {record?.clientId ? getSourceOfBooking(record.clientId) : "-"}
            </ShowValue>
          </HorizontalContainer>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Occasion:</ShowLabel>
            <ShowValue>{record?.occasion || "-"}</ShowValue>
          </HorizontalContainer>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Gender Pref:</ShowLabel>
            <ShowValue>{record?.genderPreference || "-"}</ShowValue>
          </HorizontalContainer>
        </ThreeColGrid>

        <ThreeColGrid>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Total People:</ShowLabel>
            <ShowValue>{record?.totalPeople ?? "-"}</ShowValue>
          </HorizontalContainer>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Slot Size:</ShowLabel>
            <ShowValue>{record?.slotSize != null ? `${record.slotSize} hr${record.slotSize !== 1 ? "s" : ""}` : "-"}</ShowValue>
          </HorizontalContainer>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Time Based:</ShowLabel>
            <ShowValue>{record?.isTimeBased != null ? (record.isTimeBased ? "Yes" : "No") : "-"}</ShowValue>
          </HorizontalContainer>
        </ThreeColGrid>
        <ThreeColGrid>
          {/* Slot bookings are time-based, so a dish allowance doesn't apply */}
          {record?.slotSize == null && (
            <HorizontalContainer>
              <ShowLabel style={{ marginBottom: "0px" }}>Allowed Dishes:</ShowLabel>
              <ShowValue>
                {record?.noOfAllowedDishes != null ? Number(record.noOfAllowedDishes) : "-"}
              </ShowValue>
            </HorizontalContainer>
          )}
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Sector:</ShowLabel>
            <ShowValue>{record?.sector || "-"}</ShowValue>
          </HorizontalContainer>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>People Guideline:</ShowLabel>
            <ShowValue>{record?.peopleGuideline || "-"}</ShowValue>
          </HorizontalContainer>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Dish Guideline:</ShowLabel>
            <ShowValue>{record?.dishGuideline || "-"}</ShowValue>
          </HorizontalContainer>
        </ThreeColGrid>

        <ThreeColGrid>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Rescheduled From:</ShowLabel>
            {record?.rescheduledFrom ? (
              <Link href={`/#/OneMealService/${record.rescheduledFrom}/show`}>
                {record.rescheduledFrom}
              </Link>
            ) : (
              <ShowValue>-</ShowValue>
            )}
          </HorizontalContainer>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Multi Day Chefit:</ShowLabel>
            {record?.multiDayChefitId ? (
              <Link href={`/#/MultiDayChefit/${record.multiDayChefitId}/show`}>
                {record.multiDayChefitId}
              </Link>
            ) : (
              <ShowValue>-</ShowValue>
            )}
          </HorizontalContainer>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>People Count By Cook:</ShowLabel>
            <ShowValue>{record?.peopleCountByCook ?? "-"}</ShowValue>
          </HorizontalContainer>
        </ThreeColGrid>

        {record?.cookArrivalTime && (
          <>
            <HorizontalLine />
            <HorizontalContainer>
              <BoltIcon sx={{ fontSize: 18, color: "#FF9800" }} />
              <ShowLabel style={{ marginBottom: "0px" }}>Cook Arrival Time (Instant):</ShowLabel>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 8px",
                  borderRadius: 6,
                  fontWeight: 600,
                  backgroundColor: "rgba(38, 135, 35, 0.15)",
                  color: colors.green,
                }}
              >
                {formatDateTimeWithSeconds(record.cookArrivalTime)}
              </span>
            </HorizontalContainer>
          </>
        )}

        <ThreeColGrid>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Pay After Service:</ShowLabel>
            <StatusBooleanBadge active={record?.hasOptedPayAfterService}>
              {record?.hasOptedPayAfterService ? "Yes" : "No"}
            </StatusBooleanBadge>
          </HorizontalContainer>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Instant Chefit:</ShowLabel>
            <StatusBooleanBadge active={record?.isInstantChefit}>
              {record?.isInstantChefit ? "Yes" : "No"}
            </StatusBooleanBadge>
          </HorizontalContainer>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>CheckIn OTP:</ShowLabel>
            <ShowValue>{record?.checkInOTP || "-"}</ShowValue>
          </HorizontalContainer>
        </ThreeColGrid>

        <HorizontalLine />

        <ThreeColGrid>
          <HorizontalContainer style={{ whiteSpace: "nowrap" }}>
            <UserIcon color={"#6C7C93"} width={16} height={16} />
            <ShowLabel style={{ marginBottom: "0px", whiteSpace: "nowrap" }}>Owner Name:</ShowLabel>
            {record?.owner ? (
              <Link href={`/#/AdminUsers/${record.owner}/show`} sx={{ whiteSpace: "nowrap" }}>
                {record?.ownerName || record.owner}
              </Link>
            ) : (
              <ShowValue style={{ whiteSpace: "nowrap" }}>-</ShowValue>
            )}
          </HorizontalContainer>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px", whiteSpace: "nowrap" }}>Agent Email:</ShowLabel>
            <ShowValue style={{ whiteSpace: "nowrap" }}>{record?.ownerEmail || "-"}</ShowValue>
          </HorizontalContainer>
        </ThreeColGrid>

      </WorkspacePanel>
          <br />

          {/* ===== SECTION 2: Customer Information + Location Details ===== */}
          <WorkspacePanel>
        <WorkspaceSection>
          <SectionHeader>
            <WorkSpaceTitleRow>
              <UserIcon color={colors.primary} height={24} width={24} />
              <SectionTitle>Customer Information</SectionTitle>
            </WorkSpaceTitleRow>
          </SectionHeader>
        </WorkspaceSection>

        <ThreeColGrid>
          <HorizontalContainer>
            <UserIcon color={"#6C7C93"} width={18} height={18} />
            <ShowLabel style={{ marginBottom: "0px" }}>Name:</ShowLabel>
            <ShowValue>{record?.customerName || "-"}</ShowValue>
          </HorizontalContainer>
          <HorizontalContainer>
            <CustomCallIconButton
              color="#6C7C93"
              size={18}
              toPhoneNumber={record?.customerPhoneNumber}
              userType={UserType.CUSTOMER}
              referenceId={Number(record?.id)}
              referenceModelName="ONE_MEAL_SERVICE"
            />
            <ShowLabel style={{ marginBottom: "0px" }}>Phone:</ShowLabel>
            <ShowValue>{record?.customerPhoneNumber || "-"}</ShowValue>
          </HorizontalContainer>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Rating:</ShowLabel>
            <ShowValue>{record?.customerRating || "-"}</ShowValue>
          </HorizontalContainer>
        </ThreeColGrid>

        <ThreeColGrid>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Customer Tip:</ShowLabel>
            <ShowValue>
              {record?.vendorTipDetails?.amount
                ? `₹${record.vendorTipDetails.amount} (${record.vendorTipDetails.status})`
                : "-"}
            </ShowValue>
          </HorizontalContainer>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Wallet Cash Used:</ShowLabel>
            <ShowValue>{record?.walletBalanceUsed || "-"}</ShowValue>
          </HorizontalContainer>
        </ThreeColGrid>

        <HorizontalLine />

        <WorkspaceSection>
          <SectionHeader>
            <WorkSpaceTitleRow style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <LocationIcon color={colors.primary} height={24} width={24} />
                <SectionTitle>Location Details</SectionTitle>
              </Box>
              {record?.house?.lat != null && record?.house?.long != null && (
                <Button
                  variant="outlined"
                  size="small"
                  component="a"
                  href={`https://www.google.com/maps/search/?api=1&query=${record.house.lat},${record.house.long}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontSize: "12px", textTransform: "none", borderRadius: "6px" }}
                  startIcon={<LocationIcon color="currentColor" width={14} height={14} />}
                >
                  Open in Maps
                </Button>
              )}
            </WorkSpaceTitleRow>
          </SectionHeader>
        </WorkspaceSection>

        <ThreeColGrid>
          <HorizontalContainer>
            <HouseIcon color={"#6C7C93"} width={16} height={16} />
            <ShowLabel style={{ marginBottom: "0px" }}>House ID:</ShowLabel>
            {record?.houseId ? (
              <Link href={`/#/Houses/${record.houseId}/show`}>{record.houseId}</Link>
            ) : (
              <ShowValue>-</ShowValue>
            )}
          </HorizontalContainer>
          <HorizontalContainer>
            <CityIcon color={"#6C7C93"} width={16} height={16} />
            <ShowLabel style={{ marginBottom: "0px" }}>City:</ShowLabel>
            {record?.cityId ? (
              <Link href={`/#/Cities/${record.cityId}/show`}>{record?.cityName || record.cityId}</Link>
            ) : (
              <ShowValue>-</ShowValue>
            )}
          </HorizontalContainer>
          <HorizontalContainer>
            <LocationIcon color={"#6C7C93"} width={16} height={16} />
            <ShowLabel style={{ marginBottom: "0px" }}>Area:</ShowLabel>
            {record?.areaId ? (
              <Link href={`/#/Areas/${record.areaId}/show`}>{record?.areaName || record.areaId}</Link>
            ) : (
              <ShowValue>-</ShowValue>
            )}
          </HorizontalContainer>
        </ThreeColGrid>

        <HorizontalLine />

        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Locality:</ShowLabel>
          {record?.localityId ? (
            <Link href={`/#/Localities/${record.localityId}/show`}>{record?.localityName || record.localityId}</Link>
          ) : (
            <ShowValue>-</ShowValue>
          )}
        </HorizontalContainer>

        <HorizontalLine />

        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Address:</ShowLabel>
          <ShowValue>
            {record?.house?.fullAddress ||
              record?.fullAddress ||
              [record?.localityName, record?.areaName, record?.cityName].filter(Boolean).join(", ") ||
              "-"}
          </ShowValue>
        </HorizontalContainer>

        {record?.house?.lat != null && record?.house?.long != null && (
          <>
            <HorizontalLine />
            <HorizontalContainer>
              <ShowLabel style={{ marginBottom: "0px" }}>Maps:</ShowLabel>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${record.house.lat},${record.house.long}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: "14px" }}
              >
                View on Google Maps
              </a>
            </HorizontalContainer>
          </>
        )}
      </WorkspacePanel>
          <br />

          {/* ===== SECTION 4: Cook Details ===== */}
          <WorkspacePanel>
        <WorkspaceSection>
          <SectionHeader>
            <WorkSpaceTitleRow>
              <ChefCapIcon color={colors.primary} height={24} width={24} />
              <SectionTitle>Cook Details</SectionTitle>
            </WorkSpaceTitleRow>
          </SectionHeader>
        </WorkspaceSection>

        {/* Cook Avatar row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "14px", mb: "16px" }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              backgroundColor: colors.primary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "20px", lineHeight: 1, fontFamily: "inherit" }}>
              {record?.cook?.cookFullProfile?.firstName?.[0]?.toUpperCase() || "?"}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#1e293b", lineHeight: 1.3, fontFamily: "inherit" }}>
              {cookName}
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#6C7C93", mt: "2px", fontFamily: "inherit" }}>
              {record?.isAssignedCookMg ? "MG Cook" : "Part-time"}
            </Typography>
          </Box>
        </Box>

        <ThreeColGrid>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Cook ID:</ShowLabel>
            {record?.cookId ? (
              <Link href={`/#/Cooks/${record.cookId}/show`}>{record.cookId}</Link>
            ) : (
              <ShowValue>-</ShowValue>
            )}
          </HorizontalContainer>
          <HorizontalContainer>
            <CustomCallIconButton
              color="#6C7C93"
              size={18}
              toPhoneNumber={record?.cook?.mobile}
              userType={UserType.COOK}
              referenceId={Number(record?.id)}
              referenceModelName="ONE_MEAL_SERVICE"
            />
            <ShowLabel style={{ marginBottom: "0px" }}>Phone:</ShowLabel>
            <ShowValue>{maskPhoneNumber(record?.cook?.mobile) || "-"}</ShowValue>
          </HorizontalContainer>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Cook Accepted:</ShowLabel>
            <ShowValue>{record?.numberOfCookAccepted ?? "-"}</ShowValue>
          </HorizontalContainer>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Cook Cut:</ShowLabel>
            <ShowValue style={{ fontWeight: 500 }}>₹{record?.oneMealServiceCookCut || "0"}</ShowValue>
          </HorizontalContainer>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>CX Selected Cook:</ShowLabel>
            {record?.cxSelectedCookId ? (
              <Link href={`/#/Cooks/${record.cxSelectedCookId}/show`}>
                {record.cxSelectedCookId}
              </Link>
            ) : (
              <ShowValue>-</ShowValue>
            )}
          </HorizontalContainer>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Shortlisted Cooks:</ShowLabel>
            <ShowValue>{record?.numberOfCookAllocated ?? "-"}</ShowValue>
          </HorizontalContainer>
        </ThreeColGrid>

        <ThreeColGrid>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Cook Acknowledged:</ShowLabel>
            <StatusBooleanBadge active={record?.cookAcknowledged}>
              {record?.cookAcknowledged ? "Yes" : "No"}
            </StatusBooleanBadge>
          </HorizontalContainer>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Assigned Farther Cook:</ShowLabel>
            <StatusBooleanBadge active={record?.assignedFartherCook}>
              {record?.assignedFartherCook ? "Yes" : "No"}
            </StatusBooleanBadge>
          </HorizontalContainer>
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Allow Late CheckIn:</ShowLabel>
            <StatusBooleanBadge active={record?.allowLateCheckIn}>
              {record?.allowLateCheckIn ? "Yes" : "No"}
            </StatusBooleanBadge>
          </HorizontalContainer>
        </ThreeColGrid>

        <HorizontalLine />

        <ThreeColGrid>
          <div>
            <ShowLabel style={{ marginBottom: "4px" }}>Allocator ID</ShowLabel>
            {record?.allocator ? (
              <Link href={`/#/AdminUsers/${record.allocator}/show`}>{record.allocator}</Link>
            ) : (
              <ShowValue>-</ShowValue>
            )}
          </div>
          <div>
            <ShowLabel style={{ marginBottom: "4px" }}>Allocator Name</ShowLabel>
            <ShowValue>{record?.allocatorName || "-"}</ShowValue>
          </div>
          <div>
            <ShowLabel style={{ marginBottom: "4px" }}>Allocator Email</ShowLabel>
            <ShowValue>{record?.allocatorEmail || "-"}</ShowValue>
          </div>
        </ThreeColGrid>

        {record?.customerCookRating && (
          <>
            <HorizontalLine />
            <HorizontalContainer>
              <ShowLabel style={{ marginBottom: "0px" }}>Cook Customer Rating:</ShowLabel>
              <Typography sx={{ fontWeight: 700, fontSize: "15px", color: colors.primary, fontFamily: "inherit" }}>
                {record.customerCookRating.overallRating ?? "-"}/5
              </Typography>
              {record.customerCookRating.ratingTags?.comments && (
                <>
                  <ShowLabel style={{ marginBottom: "0px" }}>Comments:</ShowLabel>
                  <ShowValue>
                    {Array.isArray(record.customerCookRating.ratingTags.comments)
                      ? record.customerCookRating.ratingTags.comments.join(", ")
                      : Object.values(record.customerCookRating.ratingTags.comments as Record<string, string>).join(", ")}
                  </ShowValue>
                </>
              )}
            </HorizontalContainer>
          </>
        )}
      </WorkspacePanel>
          <br />

          {/* ===== Meal Map ===== */}
          <TableWrapper>
        <TableTopHeader>
          <Title>Meal Map</Title>
          <Button onClick={() => setOpenAdd(true)}>Add Dish</Button>
        </TableTopHeader>

        {/* --- Customer Added --- */}
        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1e293b", letterSpacing: "0.4px", mt: "12px", mb: "10px", px: "24px" }}>
          By Customer
        </Typography>
        <Box sx={{ px: "24px", pb: "8px" }}>
          {(() => {
            const allActiveMealMap = (record?.mealMap || []).filter(
              (dish: any) => dish.deletedByType === null,
            );
            const customerMealMap = allActiveMealMap.filter(
              (dish: any) => dish.createdByType !== "COOK",
            );
            return allActiveMealMap.length ? (
              <MealMapTable
                mealMap={customerMealMap}
                omsId={record?.id}
                totalRows={allActiveMealMap.length}
              />
            ) : (
              <div style={{ padding: "12px 0", color: "#6C7C93", fontSize: "14px" }}>
                No items added by customer
              </div>
            );
          })()}
        </Box>

        {/* --- Cook Added --- */}
        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1e293b", letterSpacing: "0.4px", mt: "24px", mb: "10px", px: "24px" }}>
          By Cook
        </Typography>
        <Box sx={{ px: "24px", pb: "16px" }}>
          {(() => {
            const cookMealMap = (record?.mealMap || []).filter(
              (dish: any) =>
                (dish.createdByType === "COOK" && dish.deletedByType === null)||(dish.createdBy!=='COOK'&& dish.deletedByType===null),
            );
            return cookMealMap.length ? (
              <MealMapTable mealMap={cookMealMap} omsId={record?.id} />
            ) : (
              <div style={{ padding: "12px 0", color: "#6C7C93", fontSize: "14px" }}>
                No items added by cook
              </div>
            );
          })()}
        </Box>
      </TableWrapper>
          <br />

          {/* ===== Customer Preferences by Cook ===== */}
          {record?.cxPreferences?.length > 0 && (
            <TableWrapper>
          <TableTopHeader>
            <Title>Customer Preferences (by Cook)</Title>
          </TableTopHeader>
          <TableContainer component={Paper} sx={{ borderRadius: "8px" }}>
            <StyledTable>
              <TableHead>
                <TableRow sx={{ backgroundColor: "rgba(232, 235, 237, 0.3) !important" }}>
                  <TableHeader>Question</TableHeader>
                  <TableHeader>Answer</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {record.cxPreferences.map((pref: any, idx: number) => (
                  <CustomTableRowTR key={idx}>
                    <TableData>{pref.question}</TableData>
                    <TableData>{pref.answer}</TableData>
                  </CustomTableRowTR>
                ))}
              </TableBody>
            </StyledTable>
          </TableContainer>
        </TableWrapper>
      )}

          {/* ===== Customer Ratings ===== */}
          {record?.customerRatings?.length > 0 && (
            <>
              <br />
              <TableWrapper>
                <TableTopHeader>
                  <Title>Customer Ratings</Title>
                </TableTopHeader>
                <TableContainer component={Paper}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell align="center">Overall</TableCell>
                        <TableCell align="center">Behaviour</TableCell>
                        <TableCell align="center">Cleanliness</TableCell>
                        <TableCell align="center">Food Quality</TableCell>
                        <TableCell align="center">Comments</TableCell>
                        <TableCell align="center">Photos</TableCell>
                        <TableCell align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {record.customerRatings.map((row: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell align="center" sx={{ fontWeight: 500 }}>
                            <Chip label={row?.overallRating} />
                          </TableCell>
                          <TableCell align="center">
                            <Box>
                              {row?.behaviourRating && (
                                <Chip sx={{ mb: "4px" }} label={row.behaviourRating} />
                              )}
                              {row?.ratingTags?.["Behavior related"]?.map(
                                (val: any, ind: number) => (
                                  <Box key={ind} display="flex" flexDirection="column" mb="4px">
                                    <Chip label={val} />
                                  </Box>
                                ),
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box>
                              {row?.cleaninessRating && (
                                <Chip sx={{ mb: "4px" }} label={row.cleaninessRating} />
                              )}
                              {row?.ratingTags?.["Hygiene Related"]?.map(
                                (val: any, ind: number) => (
                                  <Box key={ind} display="flex" flexDirection="column" mb="4px">
                                    <Chip label={val} />
                                  </Box>
                                ),
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box>
                              {row?.foodQualityRating && (
                                <Chip sx={{ mb: "4px" }} label={row.foodQualityRating} />
                              )}
                              {row?.ratingTags?.["Food related"]?.map(
                                (val: any, ind: number) => (
                                  <Box key={ind} display="flex" flexDirection="column" mb="4px">
                                    <Chip label={val} />
                                  </Box>
                                ),
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">{row?.comments}</TableCell>
                          <TableCell align="center">
                            <Box display="flex" justifyContent="center" alignItems="center" gap="5px">
                              {row?.photos?.map((photo: any, ind: number) => (
                                <Box key={ind} sx={{ cursor: "pointer" }}>
                                  <ImagePreviewer value={photo} width="70px" height="70px" />
                                </Box>
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              disabled={!row?.id}
                              onClick={() => {
                                EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
                                  message: "Are you sure you want to delete this rating?",
                                  onClose: (confirmed: boolean) => {
                                    if (confirmed) {
                                      cookServiceForRating.deleteCookRating(row.id).then(() => {
                                        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                                          message: "Rating deleted successfully",
                                          type: CustomSnackbarTypes.SUCCESS,
                                        });
                                        refresh();
                                      }).catch(() => {
                                        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                                          message: "Failed to delete rating",
                                          type: CustomSnackbarTypes.ERROR,
                                        });
                                      });
                                    }
                                  },
                                  title: "Confirmation",
                                });
                              }}
                            >
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TableWrapper>
            </>
          )}

          <br />

          {/* ===== Notes ===== */}
          <WorkspacePanel>
            <WorkspaceSection>
              <SectionHeader>
                <WorkSpaceTitleRow>
                  <AlertIcon color={colors.primary} height={24} width={24} />
                  <SectionTitle>Notes</SectionTitle>
                </WorkSpaceTitleRow>
              </SectionHeader>
            </WorkspaceSection>

            {(
              [
                { key: "customerComments", title: "Customer Comments" },
                { key: "supportComments", title: "Support Comments" },
                { key: "allocationComments", title: "Allocation Comments" },
                { key: "chefFeedback", title: "Chef Feedback" },
              ] as { key: string; title: string }[]
            ).map(({ key, title }) => {
              const raw = record?.[key];
              const items: any[] = !raw
                ? []
                : Array.isArray(raw)
                ? raw
                : Object.values(raw);
              return (
                <React.Fragment key={key}>
                  <HorizontalContainer style={{ marginBottom: "8px" }}>
                    <ShowLabel style={{ marginBottom: "0px", whiteSpace: "nowrap" }}>{title}:</ShowLabel>
                    <ShowValue>
                      {items.length === 0 ? "-" : items.map((item: any) => item.comment).filter(Boolean).join(",  ")}
                    </ShowValue>
                  </HorizontalContainer>
                </React.Fragment>
              );
            })}
          </WorkspacePanel>
          <br />
          <CheckInAndOutImages />

        </LeftWorkspacePanel>

        <RightWorkspacePanel>

          {/* ===== Payment Summary ===== */}
          <WorkspacePanel>
            <WorkspaceSection>
              <SectionHeader>
                <WorkSpaceTitleRow>
                  <RecieptIcon color={colors.primary} height={24} width={24} />
                  <SectionTitle>Payment Summary</SectionTitle>
                </WorkSpaceTitleRow>
              </SectionHeader>
            </WorkspaceSection>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <ShowLabel style={{ marginBottom: "0px" }}>OMS Amount</ShowLabel>
                <ShowValue style={{ fontWeight: 500 }}>₹{record?.oneMealServiceAmount || "0"}</ShowValue>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <ShowLabel style={{ marginBottom: "0px" }}>Discount</ShowLabel>
                <ShowValue style={{ fontWeight: 500, color: record?.discount && parseFloat(record.discount) > 0 ? "#dc2626" : undefined }}>
                  {record?.discount && parseFloat(record.discount) > 0 ? `-₹${record.discount}` : "₹0"}
                </ShowValue>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <ShowLabel style={{ marginBottom: "0px" }}>Surge</ShowLabel>
                <ShowValue style={{ fontWeight: 500 }}>₹{record?.surgeAmount || "0"}</ShowValue>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <ShowLabel style={{ marginBottom: "0px" }}>Express</ShowLabel>
                <ShowValue style={{ fontWeight: 500 }}>₹{record?.expressAmount || "0"}</ShowValue>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <ShowLabel style={{ marginBottom: "0px" }}>Wallet Used</ShowLabel>
                <ShowValue style={{ fontWeight: 500 }}>₹{record?.walletBalanceUsed || "0"}</ShowValue>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <ShowLabel style={{ marginBottom: "0px" }}>Cashback Amount</ShowLabel>
                <ShowValue style={{ fontWeight: 500 }}>
                  {record?.cashback?.amount != null ? `₹${record.cashback.amount}` : "-"}
                </ShowValue>
              </Box>
            </Box>

            <HorizontalLine />

            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {record?.paymentStatus === "PAID" && (
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <ShowLabel style={{ marginBottom: "0px" }}>Amount Paid</ShowLabel>
                  <ShowValue style={{ fontWeight: 600, color: "#16a34a" }}>
                    ₹{(parseFloat(record.oneMealServiceAmount || "0") - parseFloat(record.discount || "0")).toFixed(2)}
                  </ShowValue>
                </Box>
              )}
              {record?.vendorTipDetails?.amount && (
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <ShowLabel style={{ marginBottom: "0px" }}>Customer Tip</ShowLabel>
                  <ShowValue style={{ fontWeight: 500 }}>₹{record.vendorTipDetails.amount}</ShowValue>
                </Box>
              )}
            </Box>

            <HorizontalLine />

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: "2px" }}>
              <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#1e293b", fontFamily: "inherit" }}>Net Payable</Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 700, color: colors.primary, fontFamily: "inherit" }}>
                ₹{record?.paymentStatus === "PAID"
                  ? "0"
                  : (
                      parseFloat(record?.oneMealServiceAmount || "0") -
                      parseFloat(record?.discount || "0") -
                      parseFloat(record?.walletBalanceUsed || "0")
                    ).toFixed(2)}
              </Typography>
            </Box>
          </WorkspacePanel>
          <br />

          {/* ===== Cook Arrival Tracking ===== */}
          <WorkspacePanel>
            <WorkspaceSection>
              <SectionHeader>
                <WorkSpaceTitleRow>
                  <ClockIcon color={colors.primary} height={24} width={24} />
                  <SectionTitle>Cook Arrival Tracking</SectionTitle>
                </WorkSpaceTitleRow>
              </SectionHeader>
            </WorkspaceSection>

            <HorizontalContainer style={{ marginBottom: "8px" }}>
              <ShowLabel style={{ marginBottom: "0px" }}>Arrival Status:</ShowLabel>
              <ShowValue
                style={{
                  color:
                    record?.cookArrivalStatus === "RUNNING_LATE"
                      ? "#d32f2f"
                      : record?.cookArrivalStatus === "ON_TIME"
                        ? "#2e7d32"
                        : record?.cookArrivalStatus
                          ? "#1565c0"
                          : undefined,
                  fontWeight: record?.cookArrivalStatus ? 600 : undefined,
                }}
              >
                {record?.cookArrivalStatus ?? "N/A"}
              </ShowValue>
            </HorizontalContainer>
            <HorizontalContainer style={{ marginBottom: "8px" }}>
              <ShowLabel style={{ marginBottom: "0px" }}>Status Message:</ShowLabel>
              <ShowValue>{record?.statusMessage ?? "N/A"}</ShowValue>
            </HorizontalContainer>
            <HorizontalContainer style={{ marginBottom: "8px" }}>
              <ShowLabel style={{ marginBottom: "0px" }}>ETA Arrival:</ShowLabel>
              <ShowValue>{record?.etaArrivalTime ? formatDateTime(record.etaArrivalTime) : "N/A"}</ShowValue>
            </HorizontalContainer>
            <HorizontalContainer style={{ marginBottom: "8px" }}>
              <ShowLabel style={{ marginBottom: "0px" }}>ETA Source:</ShowLabel>
              <ShowValue>{record?.etaSource ?? "N/A"}</ShowValue>
            </HorizontalContainer>
            <HorizontalContainer style={{ marginBottom: "8px" }}>
              <ShowLabel style={{ marginBottom: "0px" }}>Agent Expected Arrival:</ShowLabel>
              <ShowValue>{record?.agentExpectedArrivalTime ? formatDateTime(record.agentExpectedArrivalTime) : "N/A"}</ShowValue>
            </HorizontalContainer>

            <HorizontalLine />

            <HorizontalContainer style={{ marginBottom: "8px" }}>
              <ShowLabel style={{ marginBottom: "0px" }}>Cook Moving:</ShowLabel>
              <ShowValue>{record?.isCookMoving == null ? "N/A" : record.isCookMoving ? "Yes" : "No"}</ShowValue>
            </HorizontalContainer>
            <HorizontalContainer style={{ marginBottom: "8px" }}>
              <ShowLabel style={{ marginBottom: "0px" }}>Delayed:</ShowLabel>
              <ShowValue
                style={{
                  color: record?.isDelayed == null ? undefined : record.isDelayed ? "#d32f2f" : "#2e7d32",
                  fontWeight: record?.isDelayed != null ? 600 : undefined,
                }}
              >
                {record?.isDelayed == null ? "N/A" : record.isDelayed ? "Yes" : "No"}
              </ShowValue>
            </HorizontalContainer>
            <HorizontalContainer style={{ marginBottom: "8px" }}>
              <ShowLabel style={{ marginBottom: "0px" }}>Travel Distance:</ShowLabel>
              <ShowValue>
                {record?.travelDistance != null ? `${(record.travelDistance / 1000).toFixed(2)} km` : "N/A"}
              </ShowValue>
            </HorizontalContainer>
            <HorizontalContainer style={{ marginBottom: "8px" }}>
              <ShowLabel style={{ marginBottom: "0px" }}>Live Map:</ShowLabel>
              <ShowValue>{record?.showLiveMap == null ? "N/A" : record.showLiveMap ? "Available" : "Not Available"}</ShowValue>
            </HorizontalContainer>

            {record?.cookRecentCoordinates?.coordinates && (
              <>
                <HorizontalLine />
                <HorizontalContainer>
                  <ShowLabel style={{ marginBottom: "0px" }}>Cook Coordinates:</ShowLabel>
                  <ShowValue>
                    Lng: {record.cookRecentCoordinates.coordinates[0]}, Lat: {record.cookRecentCoordinates.coordinates[1]}
                  </ShowValue>
                </HorizontalContainer>
                <HorizontalContainer style={{ marginBottom: "8px" }}>
                  <ShowLabel style={{ marginBottom: "0px" }}>View on Map:</ShowLabel>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${record.cookRecentCoordinates.coordinates[1]},${record.cookRecentCoordinates.coordinates[0]}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: "14px" }}
                  >
                    Open in Google Maps
                  </a>
                </HorizontalContainer>
              </>
            )}
          </WorkspacePanel>
          <br />

          {/* ===== Timestamps ===== */}
          <WorkspacePanel>
            <WorkspaceSection>
              <SectionHeader>
                <WorkSpaceTitleRow>
                  <CalendarIcon color={colors.primary} height={24} width={24} />
                  <SectionTitle>Timestamps</SectionTitle>
                </WorkSpaceTitleRow>
              </SectionHeader>
            </WorkspaceSection>

            <HorizontalContainer style={{ marginBottom: "8px" }}>
              <ShowLabel style={{ marginBottom: "0px" }}>Created At:</ShowLabel>
              <ShowValue>{record?.createdAt ? formatDateTimeWithSeconds(record.createdAt) : "-"}</ShowValue>
            </HorizontalContainer>
            <HorizontalContainer style={{ marginBottom: "8px" }}>
              <ShowLabel style={{ marginBottom: "0px" }}>Updated At:</ShowLabel>
              <ShowValue>{record?.updatedAt ? formatDateTimeWithSeconds(record.updatedAt) : "-"}</ShowValue>
            </HorizontalContainer>
            <HorizontalContainer style={{ marginBottom: "8px" }}>
              <ShowLabel style={{ marginBottom: "0px" }}>Payment Updated At:</ShowLabel>
              <ShowValue>{record?.paymentStatusLastUpdatedAt ? formatDateTimeWithSeconds(record.paymentStatusLastUpdatedAt) : "-"}</ShowValue>
            </HorizontalContainer>
            <HorizontalContainer style={{ marginBottom: "8px" }}>
              <ShowLabel style={{ marginBottom: "0px" }}>Pre CheckIn At:</ShowLabel>
              <ShowValue>
                {record?.preCheckedInAt
                  ? `${formatDateTimeWithSeconds(record.preCheckedInAt)} (${record.preCheckedInType})`
                  : "-"}
              </ShowValue>
            </HorizontalContainer>
            <HorizontalContainer style={{ marginBottom: "8px" }}>
              <ShowLabel style={{ marginBottom: "0px" }}>Chef Allocation Time:</ShowLabel>
              <ShowValue>{record?.chefAllocationTime ? formatDateTimeWithSeconds(record.chefAllocationTime) : "-"}</ShowValue>
            </HorizontalContainer>
            <HorizontalContainer style={{ marginBottom: "8px" }}>
              <ShowLabel style={{ marginBottom: "0px" }}>Chef Acceptance Time:</ShowLabel>
              <ShowValue>{record?.chefAcceptanceTime ? formatDateTimeWithSeconds(record.chefAcceptanceTime) : "-"}</ShowValue>
            </HorizontalContainer>
            <HorizontalContainer style={{ marginBottom: "8px" }}>
              <ShowLabel style={{ marginBottom: "0px" }}>Check In:</ShowLabel>
              <ShowValue>{record?.checkInTime ? formatDateTimeWithSeconds(record.checkInTime) : "-"}</ShowValue>
            </HorizontalContainer>
            <HorizontalContainer style={{ marginBottom: "8px" }}>
              <ShowLabel style={{ marginBottom: "0px" }}>Check Out:</ShowLabel>
              <ShowValue>{record?.checkOutTime ? formatDateTimeWithSeconds(record.checkOutTime) : "-"}</ShowValue>
            </HorizontalContainer>
            {record?.cancelledAt && (
              <HorizontalContainer>
                <ShowLabel style={{ marginBottom: "0px" }}>Cancelled At:</ShowLabel>
                <ShowValue>{formatDateTimeWithSeconds(record.cancelledAt)}</ShowValue>
              </HorizontalContainer>
            )}
          </WorkspacePanel>
          <br />

          {/* ===== Cancellation & Reschedule ===== */}
          {(record?.cancellationReason ||
            record?.cancellationSubReason ||
            record?.rescheduleReason ||
            record?.rescheduleSubReason ||
            record?.cookRejectionReason ||
            record?.deniedBy ||
            record?.notConvertedReason ||
            record?.cancelledBy) && (
            <>
              <WorkspacePanel>
                <WorkspaceSection>
                  <SectionHeader>
                    <WorkSpaceTitleRow>
                      <AlertIcon color={colors.primary} height={24} width={24} />
                      <SectionTitle>Cancellation & Reschedule</SectionTitle>
                    </WorkSpaceTitleRow>
                  </SectionHeader>
                </WorkspaceSection>
                {record?.rescheduleReason && (
                  <HorizontalContainer style={{ marginBottom: "8px" }}>
                    <ShowLabel style={{ marginBottom: "0px" }}>Reschedule Reason:</ShowLabel>
                    <ShowValue>{record.rescheduleReason}</ShowValue>
                  </HorizontalContainer>
                )}
                {record?.rescheduleSubReason && (
                  <HorizontalContainer style={{ marginBottom: "8px" }}>
                    <ShowLabel style={{ marginBottom: "0px" }}>Reschedule Sub Reason:</ShowLabel>
                    <ShowValue>{record.rescheduleSubReason}</ShowValue>
                  </HorizontalContainer>
                )}
                {record?.cancellationReason && (
                  <HorizontalContainer style={{ marginBottom: "8px" }}>
                    <ShowLabel style={{ marginBottom: "0px" }}>Cancellation Reason:</ShowLabel>
                    <ShowValue>{record.cancellationReason}</ShowValue>
                  </HorizontalContainer>
                )}
                {record?.cancellationSubReason && (
                  <HorizontalContainer style={{ marginBottom: "8px" }}>
                    <ShowLabel style={{ marginBottom: "0px" }}>Sub Reason:</ShowLabel>
                    <ShowValue>{record.cancellationSubReason}</ShowValue>
                  </HorizontalContainer>
                )}
                {record?.cancelledBy && (
                  <HorizontalContainer style={{ marginBottom: "8px" }}>
                    <ShowLabel style={{ marginBottom: "0px" }}>Cancelled By:</ShowLabel>
                    <ShowValue>{record.cancelledBy}</ShowValue>
                  </HorizontalContainer>
                )}
                {record?.cancelledByEmail && (
                  <HorizontalContainer style={{ marginBottom: "8px" }}>
                    <ShowLabel style={{ marginBottom: "0px" }}>Cancelled By Email:</ShowLabel>
                    <ShowValue>{record.cancelledByEmail}</ShowValue>
                  </HorizontalContainer>
                )}
                {record?.cookRejectionReason && (
                  <HorizontalContainer style={{ marginBottom: "8px" }}>
                    <ShowLabel style={{ marginBottom: "0px" }}>Cook Rejection Reason:</ShowLabel>
                    <ShowValue>{record.cookRejectionReason}</ShowValue>
                  </HorizontalContainer>
                )}
                {record?.deniedBy && (
                  <HorizontalContainer style={{ marginBottom: "8px" }}>
                    <ShowLabel style={{ marginBottom: "0px" }}>Denied By:</ShowLabel>
                    <ShowValue>{record.deniedBy}</ShowValue>
                  </HorizontalContainer>
                )}
                {record?.deniedByType && (
                  <HorizontalContainer style={{ marginBottom: "8px" }}>
                    <ShowLabel style={{ marginBottom: "0px" }}>Denied By Type:</ShowLabel>
                    <ShowValue>{record.deniedByType}</ShowValue>
                  </HorizontalContainer>
                )}
                {record?.notConvertedReason && (
                  <HorizontalContainer>
                    <ShowLabel style={{ marginBottom: "0px" }}>Not Converted Reason:</ShowLabel>
                    <ShowValue>{record.notConvertedReason}</ShowValue>
                  </HorizontalContainer>
                )}
              </WorkspacePanel>
              <br />
            </>
          )}

          {/* ===== Comments Tables ===== */}
          {(
            [
              { key: "supportComments", title: "Support Comments" },
              { key: "customerComments", title: "Customer Comments" },
              { key: "allocationComments", title: "Allocation / Tracker Comments" },
              { key: "chefFeedback", title: "Chef Feedback" },
            ] as { key: string; title: string }[]
          ).map(({ key, title }) => {
            const raw = record?.[key];
            const items: any[] = !raw
              ? []
              : Array.isArray(raw)
              ? raw
              : Object.values(raw);
            return items.length > 0 ? (
              <React.Fragment key={key}>
                <TableWrapper>
                  <TableTopHeader>
                    <Title>{title}</Title>
                  </TableTopHeader>
                  <TableContainer component={Paper} sx={{ borderRadius: "8px" }}>
                    <StyledTable>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "rgba(232, 235, 237, 0.3) !important" }}>
                          <TableHeader>Comment</TableHeader>
                          <TableHeader>Commenter Name</TableHeader>
                          <TableHeader>Commenter Email</TableHeader>
                          <TableHeader>Timestamp</TableHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((item: any, idx: number) => (
                          <CustomTableRowTR key={item?.id || idx}>
                            <TableData>{item.comment || "-"}</TableData>
                            <TableData>{item.commenterName || "-"}</TableData>
                            <TableData>{item.commenterEmail || "-"}</TableData>
                            <TableData>
                              {item.timestamp ? formatDateTimeWithSeconds(item.timestamp) : "-"}
                            </TableData>
                          </CustomTableRowTR>
                        ))}
                      </TableBody>
                    </StyledTable>
                  </TableContainer>
                </TableWrapper>
                <br />
              </React.Fragment>
            ) : null;
          })}

          <div style={{ overflow: "hidden", width: "100%" }}>
            <PartyJsonViewer
              data={record}
              title="JSON Viewer"
              maxHeight="60vh"
              collapsible={false}
              enableSyntaxHighlight={true}
              enableCopy={true}
            />
          </div>

        </RightWorkspacePanel>

      </WorkspacePanelsWrapper>

      <AddMealMapDialog
        data={record?.mealMap}
        open={openAdd}
        callback={() => {
          setOpenAdd(false);
          refresh();
        }}
        omsId={Number(record?.id)}
      />
    </div>
  );
};

export default OneMealServiceMetaShow;
